/**
 * Careers Slice
 *
 * 50/50 section: image left, heading + copy + button right.
 */

import React from "react";
import { graphql } from "gatsby";
import { motion } from "framer-motion";
import { PrismicRichText } from "@prismicio/react";
import Button from "../../components/Button";
import * as styles from "./Careers.module.scss";

const CAREERS_VIDEO_PLAYBACK_RATE = 0.75;

const CareersDirectVideo = ({
  src,
  poster,
  playbackRate = CAREERS_VIDEO_PLAYBACK_RATE,
  className,
}) => {
  const videoRef = React.useRef(null);
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const setRate = () => {
      video.playbackRate = playbackRate;
    };
    video.addEventListener("loadedmetadata", setRate, { once: true });
    video.playbackRate = playbackRate;
    return () => video.removeEventListener("loadedmetadata", setRate);
  }, [playbackRate]);
  return (
    <video
      ref={videoRef}
      className={className}
      src={src}
      poster={poster}
      autoPlay
      loop
      muted
      playsInline
      aria-hidden
    />
  );
};

export const Careers = ({ slice }) => {
  const {
    media_type,
    image,
    video_url,
    video_poster,
    heading,
    copy,
    button_text,
    button_link,
    color_scheme,
  } = slice?.primary || {};

  const isVideoUrl =
    (media_type === "video_url" || media_type === "video") &&
    video_url;

  const isDirectFile =
    isVideoUrl &&
    (/\.(mp4|webm|ogg)(\?|$)/i.test(video_url || "") ||
      (video_url || "").includes("prismic.io"));

  return (
    <section
      className={styles.careers}
      data-color-scheme={color_scheme || "white"}
      id="careers"
    >
      <div className={`page-width py-06 ${styles.container}`}>
        <motion.div
          className={styles.media}
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {(isVideoUrl && isDirectFile) ? (
            <CareersDirectVideo
              src={video_url}
              poster={video_poster?.url}
              playbackRate={CAREERS_VIDEO_PLAYBACK_RATE}
              className={styles.video}
            />
          ) : (media_type === "image" && image?.url && (
              <img
                src={image.url}
                alt=""
                className={styles.image}
              />
          ))}
        </motion.div>
        <motion.div
          className={styles.content}
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {heading?.richText && (
            <PrismicRichText
              field={heading.richText}
              components={{
                heading2: ({ children }) => (
                  <h2 className={`h3 ${styles.heading}`}>{children}</h2>
                ),
              }}
            />
          )}
          {copy?.richText && (
            <div className={styles.copy}>
              <PrismicRichText field={copy.richText} />
            </div>
          )}
          {button_text && button_link?.url && (
            <div className={styles.cta}>
              <Button
                href={button_link.url}
                target={button_link.target}
                variant="solid green"
              >
                {button_text}
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export const query = graphql`
  fragment AboutPageDataBodyCareers on PrismicAboutPageDataBodyCareers {
    primary {
      media_type
      image {
        url
        alt
      }
      video_url
      video_poster {
        url
        alt
      }
      heading {
        richText
        text
      }
      copy {
        richText
        text
      }
      button_text
      button_link {
        url
        target
      }
      color_scheme
    }
  }
`;
